import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";

const BEFORE_IMG = "/images/before-1.jpg";
const AFTER_IMG = "/images/after-2.jpg";

const GALLERY = [
  { src: "/images/exterior-1.jpg", label: "Exterior Wash — Full Body" },
  { src: "/images/after-3.jpg", label: "After — Full Interior Reset" },
  { src: "/images/after-1.jpg", label: "After — Cabin Recovery" },
  { src: "/images/before-1.jpg", label: "Before — Ground-In Dirt & Debris" },
  { src: "/images/before-2.jpg", label: "Before — Embedded Grime" },
  { src: "/images/after-2.jpg", label: "After — Footwell Deep Clean" },
  { src: "/images/before-3.jpg", label: "Before — Stained Footwell" },
];

function CompareSlider() {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div
      ref={ref}
      data-testid="before-after-slider"
      className="relative w-full aspect-[4/5] sm:aspect-[16/10] rounded-2xl overflow-hidden border border-white/10 select-none cursor-ew-resize touch-none"
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <img
        src={AFTER_IMG}
        alt="After — freshly detailed red interior footwell"
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={BEFORE_IMG}
          alt="Before — dirty footwell with debris"
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </div>

      <span
        data-testid="before-label"
        className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-black/70 backdrop-blur px-3 py-1.5 rounded-full text-slate-300 border border-white/10"
      >
        Before
      </span>
      <span
        data-testid="after-label"
        className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-amber-500 px-3 py-1.5 rounded-full text-black font-semibold"
      >
        After
      </span>

      <div
        className="absolute top-0 bottom-0 w-px bg-amber-400"
        style={{ left: `${pos}%` }}
      >
        <div
          data-testid="slider-handle"
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-amber-500 text-black flex items-center justify-center shadow-[0_0_30px_rgba(245,158,11,0.5)]"
        >
          <MoveHorizontal size={20} strokeWidth={2.5} />
        </div>
      </div>
    </div>
  );
}

export default function BeforeAfterGallery() {
  return (
    <section id="results" data-testid="gallery-section" className="relative py-24 sm:py-36 bg-[#0e1117]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14 sm:mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
        >
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400" data-testid="gallery-eyebrow">
              02 — Results
            </span>
            <h2 className="mt-4 font-syne font-extrabold uppercase tracking-tight leading-none text-4xl sm:text-6xl text-white">
              Drag The <span className="text-amber-400">Difference</span>
            </h2>
          </div>
          <p className="max-w-sm text-slate-400 text-base">
            Real P&H work. Dog hair, ground-in stains, months of road grime —
            slide to see what a detail brings back.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
        >
          <CompareSlider />
        </motion.div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {GALLERY.map((g, i) => (
            <motion.figure
              key={g.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              data-testid={`gallery-item-${i}`}
              className="group relative aspect-[4/5] sm:aspect-[4/3] rounded-xl overflow-hidden border border-white/10"
            >
              <img
                src={g.src}
                alt={g.label}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute bottom-3 left-3 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                {g.label}
              </figcaption>
            </motion.figure>
          ))}
          <motion.a
            href="sms:9197373526?&body=Hi%20P%26H%20Detailing!%20I%27d%20like%20to%20book%20a%20detail."
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ delay: 0.7, duration: 0.6 }}
            data-testid="gallery-cta-tile"
            className="group relative aspect-[4/5] sm:aspect-[4/3] rounded-xl border border-dashed border-amber-500/40 hover:border-amber-400 flex flex-col items-center justify-center gap-3 text-center p-6 transition-colors duration-500 hover:bg-amber-500/5"
          >
            <span className="font-syne font-extrabold uppercase text-xl sm:text-2xl text-white leading-tight">
              Your Vehicle <span className="text-amber-400">Next</span>
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">
              Text 919-737-3526
            </span>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
